import { useEffect, useState, type ReactNode } from 'react';
import type { SiteAuthRuntime, SiteUserSession } from '../types';
import {
  changeBlissPassword,
  clearStoredSession,
  getCurrentBlissSession,
  getSiteAuthRuntime,
  getStoredSession,
  registerWithBliss,
  signInWithBliss,
} from '../services/bliss-app-auth';
import { SiteAuthContext } from './siteAuthContext';

export function SiteAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SiteUserSession | null>(() => getStoredSession());
  const [runtime, setRuntime] = useState<SiteAuthRuntime | null>(null);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    let isActive = true;

    async function initializeAuth() {
      try {
        const nextRuntime = await getSiteAuthRuntime();
        if (isActive) {
          setRuntime(nextRuntime);
          setRuntimeError(null);
        }

        const nextSession = await getCurrentBlissSession();
        if (isActive) {
          setSession(nextSession);
        }
      } catch (error) {
        clearStoredSession();

        if (isActive) {
          setSession(null);
          setRuntimeError(
            error instanceof Error
              ? error.message
              : 'The Victoria\'s Place portal could not connect to the Bliss runtime.',
          );
        }
      } finally {
        if (isActive) {
          setIsInitializing(false);
        }
      }
    }

    void initializeAuth();

    return () => {
      isActive = false;
    };
  }, []);

  async function signIn(email: string, password: string) {
    const nextSession = await signInWithBliss(email, password);
    setSession(nextSession);
    setRuntimeError(null);

    if (!runtime) {
      try {
        const nextRuntime = await getSiteAuthRuntime();
        setRuntime(nextRuntime);
        setRuntimeError(null);
      } catch (error) {
        setRuntimeError(error instanceof Error ? error.message : 'Unable to read the Bliss runtime configuration.');
      }
    }

    return nextSession;
  }

  async function signUp(input: {
    email: string;
    password: string;
    name?: string;
    role: 'vendor' | 'resource_vendor' | 'adult_model' | 'parent' | 'donor';
  }) {
    const nextSession = await registerWithBliss(input);
    setSession(nextSession);
    setRuntimeError(null);

    if (!runtime) {
      try {
        const nextRuntime = await getSiteAuthRuntime();
        setRuntime(nextRuntime);
        setRuntimeError(null);
      } catch (error) {
        setRuntimeError(error instanceof Error ? error.message : 'Unable to read the Bliss runtime configuration.');
      }
    }

    return nextSession;
  }

  function signOut() {
    clearStoredSession();
    setSession(null);
  }

  async function refreshSession() {
    try {
      const nextSession = await getCurrentBlissSession();
      setSession(nextSession);
      return nextSession;
    } catch (error) {
      clearStoredSession();
      setSession(null);
      throw error;
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    await changeBlissPassword(currentPassword, newPassword);
  }

  return (
    <SiteAuthContext.Provider
      value={{
        session,
        isAuthenticated: !!session,
        isInitializing,
        runtime,
        runtimeError,
        signIn,
        signUp,
        signOut,
        refreshSession,
        changePassword,
      }}
    >
      {children}
    </SiteAuthContext.Provider>
  );
}
